var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { datadogLogs } from '@datadog/browser-logs';
import React, { useEffect, useMemo } from 'react';
import { RouterProvider } from 'react-router';
import ErrorBoundary from './ErrorBoundary';
import ContextProvider from './LinkApiRouter/contexts/index.context';
import LinkModal from './LinkModal';
import LinkThemeProvider from './LinkThemeProvider';
import { getRouter } from './Router/Router';
import { requestGetSearch } from './clients/hub-api';
import Ctx from './context';
import useApp from './hooks/useApp';
import SandboxProvider from './hooks/useSandbox';
import { useAppDispatch, useAppSelector } from './state';
import { hubApi } from './state/services/hub-api';
import datadogStartView from './tracking/datadogRum';
import { EventSurface, EventType } from './types/events';
import { setFetchWithCatch } from './utils';
import SearchProvider from './views/SearchLegacy/Search.context';
import { selectFeatureFlags } from './state/slices/slice-link-params';
import useHandleLinkApiResponse from './hooks/useHandleLinkApiResponse';
import useNavigationListener from './hooks/useNavigationListener';
import { useSDKExceptionListener } from './hooks/useSDKExceptionListener';
import { useArtifactsInterval } from './hooks/useArtifactsInterval';
var App = function (_a) {
    var opts = _a.opts, unMount = _a.unMount, linkTokenPayload = _a.linkTokenPayload, modalSessionId = _a.modalSessionId, _b = _a.error, _error = _b === void 0 ? null : _b, _c = _a.open, _open = _c === void 0 ? true : _c, children = _a.children;
    var _d = useApp(opts, linkTokenPayload, modalSessionId, _error, _open), newOpts = _d.opts, setTopPlatformsImages = _d.otherValues.setTopPlatformsImages;
    var linkToken = newOpts.linkToken, fireClientEvent = newOpts.fireClientEvent, setError = newOpts.setError, currentSource = newOpts.currentSource, fireUnifiedEvent = newOpts.fireUnifiedEvent, fireTrackEvent = newOpts.fireTrackEvent;
    var dispatch = useAppDispatch();
    var _e = useArtifactsInterval(), clearArtifactsInterval = _e.clearArtifactsInterval, startArtifactsInterval = _e.startArtifactsInterval;
    useEffect(function () {
        function fetchData() {
            return __awaiter(this, void 0, void 0, function () {
                var module;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, import(
                            /*
                              webpackChunkName: "top-platforms",
                              webpackPrefetch: true
                            */
                            './consts/top-platforms')];
                        case 1:
                            module = _a.sent();
                            setTopPlatformsImages(module.default);
                            return [2 /*return*/];
                    }
                });
            });
        }
        void fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        setFetchWithCatch({
            fireClientEvent: fireClientEvent,
            setError: setError,
            navigateToError: function () { return getRouter().navigate('/error'); },
        });
    }, [fireClientEvent, setError]);
    useNavigationListener();
    // Preload initial provider results, so we have time to load the initial providers list
    // and avoid showing the user an empty grid or loading state.
    // TODO before merging: This may belong somewhere else?
    useEffect(function () {
        void dispatch(hubApi.endpoints.getInitialProviders.initiate({}, {
            forceRefetch: true,
        }));
    }, [dispatch, linkToken, linkTokenPayload.mode]);
    var currentSurface = useMemo(function () {
        var isInit = currentSource === EventSurface.MODAL_INITIALIZATION;
        return isInit ? EventSurface.MODAL_INITIALIZATION : currentSource;
    }, [currentSource]);
    useEffect(function () { return datadogStartView(currentSurface); }, [currentSurface]);
    useEffect(function () {
        fireUnifiedEvent(EventType.LINK_LAUNCH_EVENT);
        fireUnifiedEvent(EventType.LINK_FF_EVENT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        var handleVisibilityChange = function (event) {
            var eventType = event.target.visibilityState === 'visible'
                ? EventType.LINK_FOREGROUNDED
                : EventType.LINK_BACKGROUNDED;
            fireUnifiedEvent(eventType);
            if (eventType === EventType.LINK_BACKGROUNDED) {
                clearArtifactsInterval();
            }
            // deprecated: TODO: clean up after a short overlap
            datadogLogs.logger.info("Client visibility changed, document.hidden: ".concat(document.hidden), event);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return function () {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fireUnifiedEvent, startArtifactsInterval, clearArtifactsInterval]);
    var featureFlags = useAppSelector(selectFeatureFlags);
    useEffect(function () {
        if (!linkTokenPayload.accountId) {
            // Need to add '' to leverage memoization of search endpoint
            void requestGetSearch(linkTokenPayload.mode, opts.linkToken, '', linkTokenPayload.platformType);
            // Commenting out this request right now (March 3, 2023).
            // This request in staging is taking about a minute to return with ~28 MB of data.
            // void requestGetPlatforms(
            //   linkTokenPayload.mode,
            //   opts.linkToken,
            //   linkTokenPayload.platformType
            // );
        }
        fireTrackEvent('Link Open', undefined, true);
        fireClientEvent('open');
        opts.postParentWindowMessage({
            type: 'PINWHEEL_LOAD_COMPLETE',
            payload: {
                // Enables Portal to programmatically focus the iframe element, in
                // order to address focus/selection issues on Safari/iOS VoiceOver.
                // Context: https://github.com/underdog-tech/portal/pull/131
                // For now, this functionality is gated behind a FF in case it causes
                // any unexpected regressions and we need to revert.
                focusIframe: featureFlags.focusIframeOnLoadFromPortal,
            },
        });
        // TODO: PP-5346
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useHandleLinkApiResponse();
    useSDKExceptionListener();
    return (React.createElement(Ctx.Provider, { value: newOpts },
        React.createElement(LinkThemeProvider, { useThemeNext: featureFlags.linkOrigamiThemeNext, useDarkMode: featureFlags.allowLinkDarkMode && opts.useDarkMode },
            React.createElement(ErrorBoundary, null,
                React.createElement(ContextProvider, null,
                    React.createElement(SearchProvider, null,
                        React.createElement(SandboxProvider, null,
                            React.createElement(LinkModal, { unMount: unMount },
                                React.createElement(RouterProvider, { router: getRouter() })),
                            children)))))));
};
export default App;
